import type { AnchorHTMLAttributes, FC, ReactNode } from 'react'
import type { PixelPalette } from '@/lib/pixel'

import { cn } from '@/lib/utils'
import { PixelSprite } from '@/components/pixel/pixel-sprite'

import { ICON_ARROW } from '@/constants/sprites'

type PixelButtonVariant = 'pink' | 'paper'

type PixelButtonSize = 'sm' | 'md' | 'lg'

interface PixelButtonStyleOptions {
    variant?: PixelButtonVariant
    size?: PixelButtonSize
    className?: string
}

interface PixelButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    variant?: PixelButtonVariant
    size?: PixelButtonSize
    arrow?: boolean
}

interface PixelArrowProps {
    className?: string
}

const CURRENT_COLOR: PixelPalette = { K: 'currentColor' }

const VARIANTS: Record<PixelButtonVariant, string> = {
    pink: 'bg-bubblegum hover:bg-[#ea6aab]',
    paper: 'bg-white hover:bg-paper'
}

const SIZES: Record<PixelButtonSize, string> = {
    sm: 'h-10 gap-2 px-4 text-base',
    md: 'h-12 gap-2.5 px-6 text-lg',
    lg: 'h-14 gap-3 px-7 text-xl md:h-16 md:px-9 md:text-2xl'
}

/** Class list for the chunky pixel button, shared by links and real buttons. */
export const pixelButtonClass = ({
    variant = 'pink',
    size = 'md',
    className
}: PixelButtonStyleOptions = {}): string => {
    return cn(
        'group/button inline-flex shrink-0 cursor-pointer select-none items-center justify-center whitespace-nowrap border-2 border-ink font-heading leading-none text-ink shadow-pixel',
        'transition-[translate,box-shadow,background-color] duration-150 ease-[steps(2,end)]',
        'hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_var(--ink)]',
        'active:translate-x-1 active:translate-y-1 active:shadow-none',
        'disabled:pointer-events-none disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

export const PixelArrow: FC<PixelArrowProps> = ({ className }): ReactNode => {
    return (
        <PixelSprite
            rows={ICON_ARROW}
            palette={CURRENT_COLOR}
            className={cn('h-[0.62em] w-auto group-hover/button:animate-nudge', className)}
        />
    )
}

export const PixelButton: FC<PixelButtonProps> = ({
    variant,
    size,
    arrow = false,
    className,
    children,
    ...rest
}): ReactNode => {
    return (
        <a className={pixelButtonClass({ variant, size, className })} {...rest}>
            {children}
            {arrow && <PixelArrow />}
        </a>
    )
}
